import React, { ReactElement } from 'react'

import { useDebouncedCallback } from 'use-debounce'
import styled, { css } from 'styled-components'

import { variables } from 'src/Styled/variables'
import Loader from 'src/Assets/Svg/loader.svg?react'
import { DEBOUNCE_DELAY_100 } from 'src/Constants/constants'

export interface DangerButtonProps extends React.DetailedHTMLProps<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
> {
  loading?: boolean
}

const StyledButton = styled.button<{
  $loading?: boolean
}>`
  position: relative;
  display: flex;
  height: 48px;
  padding: 8px 24px;
  justify-content: center;
  align-items: center;
  border-radius: 40px;
  gap: 4px;
  background: ${({ theme }) => theme.color.mediumRed};
  border: 1px solid ${({ theme }) => theme.color.mediumRed};
  color: ${({ theme }) => theme.unchangingColor.white};

  font-family: 'Segoe UI';
  font-size: ${variables.fontSize14};
  font-style: normal;
  font-weight: ${variables.fontWeight400};
  line-height: 150%;
  cursor: pointer;

  transition: all 0.2s;

  svg {
    path {
      fill: ${({ theme }) => theme.unchangingColor.white};
    }
  }

  &:hover {
    opacity: 0.85;
    background: ${({ theme }) => theme.color.mediumRed};
  }

  &:focus-visible {
    outline: 1px solid ${({ theme }) => theme.color.mediumRed};
    outline-offset: -1px;
    opacity: 0.85;
  }

  &:active {
    outline: none;
    opacity: 0.7;
    background: ${({ theme }) => theme.color.mediumRed};
    border: 1px solid ${({ theme }) => theme.color.mediumRed};
    color: ${({ theme }) => theme.unchangingColor.white};

    svg {
      path {
        fill: ${({ theme }) => theme.unchangingColor.white};
      }
    }

    &:hover {
      box-shadow: none;
    }
  }

  ${({ $loading }) => {
    if (!$loading) {
      return css`
        &:disabled {
          cursor: not-allowed;
          box-shadow: none;
          background: ${({ theme }) => theme.color.mediumGrey};
          border: 1px solid ${({ theme }) => theme.color.mediumGrey};
        }
      `
    }

    return css`
      &:hover {
        box-shadow: none;
      }
    `
  }}
`

const LoaderWrapper = styled.div`
  position: absolute;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;

  svg {
    width: 17px;
    height: 17px;
  }
`

const ChildrenWrapper = styled.div<{ $loading?: boolean }>`
  opacity: ${({ $loading }) => ($loading ? 0 : 1)};
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
`

export const DangerButton: React.FC<DangerButtonProps> = (
  props,
): ReactElement => {
  const { children, loading, onClick, disabled, ...restProps } = props

  const debouncedOnClick = useDebouncedCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (typeof onClick === 'function') {
        onClick(event)
      }
    },
    DEBOUNCE_DELAY_100,
  )
  return (
    <StyledButton
      {...restProps}
      onClick={(e) => debouncedOnClick(e)}
      disabled={disabled || loading}
    >
      {loading && !disabled && (
        <LoaderWrapper>
          <Loader data-testid='loader' />
        </LoaderWrapper>
      )}

      <ChildrenWrapper $loading={loading}>{children}</ChildrenWrapper>
    </StyledButton>
  )
}
